/**
 * Uma linha da paleta de busca (Story 18.1, AC-6 e AC-8).
 *
 * ⚠️ SEM MÉRITO NA LINHA, e é decisão contratual do owner (README do epic 18).
 * A cauda de uma candidatura é só a pílula de etapa: um profissional sem
 * avaliação fica indistinguível de um com nota 4,9, e varrer a lista pela nota
 * volta a ser trabalho das LISTAGENS, onde a luz do mérito continua. Foi por
 * isso que `score` saiu do payload da rota — não há aqui nada que o desenhe.
 *
 * ⚠️ O REALCE É `item-glass`, NEUTRO. Nenhum `bg-accent` em `hover`, `focus` ou
 * `data-highlighted`: o verde deste produto é ação e mérito, e um realce verde
 * numa lista mista diria "esta linha é boa" sobre uma linha que só está sob o
 * cursor. Travado no CI (`screen-contract.test.ts`, constante `OVERLAYS`).
 */
import type { ReactElement } from 'react'
import {
  Briefcase,
  Building2,
  ClipboardCheck,
  FileText,
  IdCard,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'
import { StagePill } from '@/components/lumen/stage-pill'
import { Pill } from '@/components/ui/pill'
import { statusMeta } from '@/lib/application-labels'
import type { SearchItem, SearchKind } from '@/lib/api/global-search'

export interface SearchKindMeta {
  /** O rótulo do trilho de escopo e do cabeçalho do grupo. */
  label: string
  /** O rótulo de UMA linha — é o que viaja na cauda dos "Recentes". */
  one: string
  icon: LucideIcon
  /** A listagem da entidade, para o estado vazio poder oferecer uma saída. */
  list: string
}

/**
 * O vocabulário e os glifos das seis entidades.
 *
 * ⚠️ OS ÍCONES SÃO OS DO TRILHO DE NAVEGAÇÃO (`app-sidebar.tsx`), e não os do
 * protótipo. O protótipo desenhava com um conjunto próprio porque não tinha a
 * app ao lado; aqui, um glifo de candidatura diferente do que está no trilho
 * ensinaria dois símbolos para a mesma coisa a três centímetros de distância.
 *
 * Vive neste ficheiro — o mais folha dos dois — porque o diálogo importa a
 * linha, e não o contrário: pôr o mapa no diálogo faria a linha importar o
 * diálogo de volta.
 */
export const SEARCH_KIND_META: Record<SearchKind, SearchKindMeta> = {
  application: {
    label: 'Candidaturas',
    one: 'Candidatura',
    icon: ClipboardCheck,
    list: '/applications',
  },
  professional: {
    label: 'Profissionais',
    one: 'Profissional',
    icon: Users,
    list: '/professionals',
  },
  opportunity: { label: 'Vagas', one: 'Vaga', icon: Briefcase, list: '/opportunities' },
  customer: { label: 'Clientes', one: 'Cliente', icon: Building2, list: '/customers' },
  jobProfile: {
    label: 'Perfis de vaga',
    one: 'Perfil de vaga',
    icon: IdCard,
    list: '/job-profiles',
  },
  assessment: {
    label: 'Modelos de avaliação',
    one: 'Modelo',
    icon: FileText,
    list: '/assessments',
  },
}

/**
 * Marcas de combinação — o que sobra do `NFD` depois de tirar a letra base.
 *
 * Escrito por CODEPOINT e não com os caracteres literais: uma classe de
 * caracteres com marcas soltas dentro é invisível no editor e sobrevive mal a
 * uma cópia entre ficheiros.
 */
const MARCAS = /[\u0300-\u036f]/g

/**
 * Dobra acentos e caixa, preservando a CONTAGEM de caracteres.
 *
 * A contagem importa: os índices calculados sobre o texto normalizado são
 * usados para fatiar o texto ORIGINAL. `NFD` separa a letra da marca e a
 * remoção das marcas devolve a mesma quantidade de caracteres-base — é isso que
 * torna o índice reutilizável. É a mesma dobra que o `semAcentos` do servidor
 * faz em SQL, só que aqui basta para decidir onde pintar.
 */
function dobrar(texto: string): string {
  return texto.normalize('NFD').replace(MARCAS, '').toLowerCase()
}

export interface SearchTextPiece {
  text: string
  hit: boolean
}

/**
 * Parte o texto no trecho que casou com o termo.
 *
 * ⚠️ O REALCE É PESO E TINTA, NUNCA ACCENT (AC-8). Um `<mark>` amarelo ou verde
 * aqui seria a terceira linguagem de destaque na mesma tela.
 */
export function splitByTerm(texto: string, termo: string): SearchTextPiece[] {
  const agulha = dobrar(termo).trim()
  if (!agulha) return [{ text: texto, hit: false }]
  const alvo = dobrar(texto)
  const i = alvo.indexOf(agulha)
  if (i === -1) return [{ text: texto, hit: false }]
  return [
    { text: texto.slice(0, i), hit: false },
    { text: texto.slice(i, i + agulha.length), hit: true },
    { text: texto.slice(i + agulha.length), hit: false },
  ].filter((p) => p.text.length > 0)
}

export interface SearchResultRowProps {
  item: SearchItem
  /** O índice na lista PLANA — é ele que o `aria-activedescendant` aponta. */
  index: number
  active: boolean
  /** O termo digitado, para o realce. Vazio na lista de "Recentes". */
  term: string
  /** Numa linha de "Recentes" a cauda diz de que entidade ela é. */
  isRecent: boolean
  onOpen: (item: SearchItem) => void
  onForget: (item: SearchItem) => void
  onHover: (index: number) => void
}

export function SearchResultRow({
  item,
  index,
  active,
  term,
  isRecent,
  onOpen,
  onForget,
  onHover,
}: SearchResultRowProps): ReactElement {
  const meta = SEARCH_KIND_META[item.kind]
  const Icon = meta.icon
  const pedacos = splitByTerm(item.lead, term)

  return (
    <div
      role="option"
      id={`gs-opt-${index}`}
      data-idx={index}
      aria-selected={active}
      {...(active ? { 'data-highlighted': '' } : {})}
      // A mão vem da regra base do `globals.css` (`[role=option]`); não há
      // `cursor-*` nenhum aqui, de propósito — um `cursor-default` venceria essa
      // regra e devolveria a seta a uma superfície que se clica.
      className="item-glass flex min-h-[44px] w-full items-center gap-[11px] rounded-sm px-2.5"
      onClick={() => onOpen(item)}
      onMouseMove={() => onHover(index)}
    >
      <span
        aria-hidden="true"
        className="bg-sunk border-hair text-ink-3 rounded-xs grid h-[26px] w-[26px] shrink-0 place-items-center border"
      >
        <Icon size={14} />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="text-ink-2 truncate text-[13.5px] font-medium">
          {pedacos.map((p, n) =>
            p.hit ? (
              <mark key={n} className="text-ink bg-transparent font-[650]">
                {p.text}
              </mark>
            ) : (
              <span key={n}>{p.text}</span>
            ),
          )}
        </span>
        {item.sub ? <span className="text-ink-3 truncate text-[11.5px]">{item.sub}</span> : null}
      </span>

      <span className="ml-auto flex shrink-0 items-center gap-2.5 pl-1.5">
        {/* Nas linhas de "Recentes" o rótulo da entidade viaja na PRÓPRIA linha:
            o cabeçalho do grupo diz "Recentes", não diz de que tipo é cada item.
            Sete itens de seis tipos sem isto são adivinhação. */}
        {isRecent ? (
          <span className="text-ink-4 font-mono text-[11px] tabular-nums">{meta.one}</span>
        ) : null}

        {item.kind === 'application' ? (
          <StagePill name={item.currentStageName} position={item.currentStagePosition} />
        ) : null}

        {/* O DESFECHO, e só quando é um. Uma candidatura `active` já diz o que
            interessa pela etapa, e uma segunda pílula a dizer "Ativa" em quase
            todas as linhas encheria a cauda sem acrescentar nada — a 44px de
            altura e 760px de largura, o espaço que ela ocupa sai do nome.
            Reprovada, Contratada e Retirada são o oposto: são exactamente a
            informação que faz alguém parar antes de abrir a linha errada.

            O rótulo e o tom saem do `statusMeta()` que a listagem usa. Escrevê-los
            aqui à mão faria a busca e a lista discordarem no dia em que um deles
            mudasse — e seria a busca a mentir, porque é o sítio onde se olha
            depressa. */}
        {item.kind === 'application' && item.status !== 'active'
          ? (() => {
              const estado = statusMeta(item.status)
              return <Pill variant={estado.variant}>{estado.label}</Pill>
            })()
          : null}

        {isRecent ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Esquecer ${item.lead}`}
            title="Esquecer (⌫)"
            className="text-ink-4 hover:text-error hover:bg-error-soft rounded-xs grid h-[22px] w-[22px] place-items-center"
            onClick={(e) => {
              // Sem isto o clique no `×` abriria o registo que ele acabou de
              // esquecer — o `onClick` da linha está por baixo.
              e.stopPropagation()
              onForget(item)
            }}
          >
            <X size={12} />
          </button>
        ) : null}
      </span>
    </div>
  )
}
